import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings, Calendar, Bell } from "lucide-react";
import { VendorServiceDetails } from "@/components/vendor/VendorServiceDetails";
import CalendarSettingsTab from "@/components/admin/calendar/CalendarSettingsTab";
import VendorNotifications from "@/pages/VendorNotifications";
import { useVendorService } from "@/hooks/useVendorService";
import { useServiceEditAccess } from "@/hooks/useServiceEditAccess";
import { LoadingScreen } from "@/components/admin/dashboard/LoadingScreen";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const VendorSettings = () => {
  const { id: serviceId } = useParams();
  const { loading: serviceLoading, serviceData } = useVendorService(serviceId);
  const { canEditBasicInfo, canAccessCalendarSettings, isLoading: permissionLoading } = useServiceEditAccess(serviceId || "");
  
  // Set default tab based on permissions
  const defaultTab = canEditBasicInfo ? "service-details" : "calendar-settings";
  const [activeTab, setActiveTab] = useState(defaultTab);

  if (serviceLoading || permissionLoading) {
    return <LoadingScreen />;
  }

  if (!serviceData) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load service data. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Service Settings
        </h1>
        <p className="text-muted-foreground">
          Manage your service details, calendar settings, and notification preferences.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`grid w-full ${canEditBasicInfo ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {canEditBasicInfo && (
            <TabsTrigger value="service-details" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Service Details
            </TabsTrigger>
          )}
          <TabsTrigger value="calendar-settings" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Calendar Settings
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {canEditBasicInfo && (
          <TabsContent value="service-details" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Service Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <VendorServiceDetails service={serviceData} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="calendar-settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Calendar & Scheduling
              </CardTitle>
            </CardHeader>
            <CardContent>
              {canAccessCalendarSettings ? (
                <CalendarSettingsTab serviceId={serviceId} />
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You don't have permission to access calendar settings. Please contact an administrator.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <VendorNotifications />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VendorSettings;